/**
 * Datadog Custom Timing Utilities
 *
 * This module provides utility functions for tracking custom business metrics
 * and performance timings in Datadog RUM. These utilities work across all pages
 * and components in the application.
 *
 * */

import { datadogRum } from "@datadog/browser-rum";
import { DD_RUM_ENABLED } from "@shared/env";

/**
 * User context data structure
 */
export interface UserContextData {
  id?: string | number;
  email?: string;
  name?: string;
  organizationId?: string | number;
  organizationName?: string;
  [key: string]: unknown;
}

/**
 * Check if Datadog RUM is enabled and initialized
 */
function isRumEnabled(): boolean {
  return typeof window !== "undefined" && DD_RUM_ENABLED === "true";
}

/**
 * Mark when a page starts loading
 * Call this at the beginning of your page component's useEffect
 *
 * @param pageName - Unique identifier for the page (e.g., 'event_detail', 'checkout')
 * @param context - Additional context data
 */
export function markPageStart(
  pageName: string,
  context?: Record<string, unknown>
): void {
  if (!isRumEnabled()) return;

  try {
    datadogRum.addAction(`page_start_${pageName}`, {
      page: pageName,
      timestamp: Date.now(),
      ...context,
    });

    // Store start time in session storage for duration calculation
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(
        `dd_page_start_${pageName}`,
        Date.now().toString()
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to mark page start:", error);
  }
}

/**
 * Mark when the first content is painted on the page
 * Call this when the main content becomes visible to the user
 *
 * @param pageName - Same identifier used in markPageStart
 * @param context - Additional context data
 */
export function markFirstContentPainted(
  pageName: string,
  context?: Record<string, unknown>
): void {
  if (!isRumEnabled()) return;

  try {
    const now = Date.now();
    let duration: number | undefined;

    // Calculate duration from page start
    if (typeof sessionStorage !== "undefined") {
      const startTime = sessionStorage.getItem(`dd_page_start_${pageName}`);
      if (startTime) {
        duration = now - parseInt(startTime, 10);
      }
    }

    datadogRum.addTiming(`first_content_painted_${pageName}`);

    datadogRum.addAction(`first_content_painted_${pageName}`, {
      page: pageName,
      timestamp: now,
      duration_ms: duration,
      ...context,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to mark first content painted:", error);
  }
}

/**
 * Mark when all components on the page are ready
 * Call this when all data is loaded and the page is fully interactive
 *
 * @param pageName - Same identifier used in markPageStart
 * @param context - Additional context data
 */
export function markAllComponentsReady(
  pageName: string,
  context?: Record<string, unknown>
): void {
  if (!isRumEnabled()) return;

  try {
    const now = Date.now();
    let duration: number | undefined;

    // Calculate duration from page start
    if (typeof sessionStorage !== "undefined") {
      const startTime = sessionStorage.getItem(`dd_page_start_${pageName}`);
      if (startTime) {
        duration = now - parseInt(startTime, 10);
        // Clean up the stored start time
        sessionStorage.removeItem(`dd_page_start_${pageName}`);
      }
    }

    datadogRum.addTiming(`all_components_ready_${pageName}`);

    datadogRum.addAction(`all_components_ready_${pageName}`, {
      page: pageName,
      timestamp: now,
      duration_ms: duration,
      ...context,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to mark all components ready:", error);
  }
}

/**
 * Measure the render time of a component
 * This is a higher-order function that wraps a component
 *
 * IMPORTANT: This function only measures synchronous execution time of the render function.
 * It does NOT account for:
 * - Async API calls inside the component
 * - Data fetching with useEffect
 * - Loading states or suspense boundaries
 * - Time until actual content is visible to the user
 *
 * For components with async data loading, consider tracking timing at the page level
 * using markPageStart(), markFirstContentPainted(), and markAllComponentsReady() instead.
 *
 * @param componentName - Name of the component being measured
 * @param renderFn - Function that renders the component
 * @returns The result of the render function
 */
export function measureComponentRender<T>(
  componentName: string,
  renderFn: () => T
): T {
  if (!isRumEnabled()) {
    return renderFn();
  }

  const startTime = performance.now();
  try {
    const result = renderFn();
    const duration = performance.now() - startTime;

    datadogRum.addAction(`component_render_${componentName}`, {
      component: componentName,
      duration_ms: duration,
    });

    // Log slow renders (> 100ms)
    if (duration > 100) {
      datadogRum.addAction(`slow_component_render`, {
        component: componentName,
        duration_ms: duration,
        threshold_ms: 100,
      });
    }

    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to measure component render for ${componentName}:`,
      error
    );
    throw error;
  }
}

/**
 * Set user context for all subsequent RUM events
 * This should be called when user data becomes available (e.g., after login)
 *
 * @param userData - User context data
 */
export function setUserContext(userData: UserContextData): void {
  if (!isRumEnabled()) return;

  try {
    // Set user in Datadog RUM
    if (userData.id) {
      const { id, ...restUserData } = userData;
      datadogRum.setUser({
        id: String(id),
        email: userData.email,
        name: userData.name,
        ...restUserData,
      });
    } else {
      // Clear user context for anonymous users
      datadogRum.clearUser();
      datadogRum.setUserProperty("user_type", "anonymous");
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to set user context:", error);
  }
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearUserContext(): void {
  if (!isRumEnabled()) return;

  try {
    datadogRum.clearUser();
    datadogRum.setUserProperty("user_type", "anonymous");
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to clear user context:", error);
  }
}

/**
 * Add a custom business metric
 * Use this to track any custom event or metric in your application
 *
 * @param metricName - Name of the metric (e.g., 'ticket_added_to_cart', 'checkout_completed')
 * @param value - Numeric value for the metric (optional)
 * @param context - Additional context data
 */
export function addCustomMetric(
  metricName: string,
  value?: number,
  context?: Record<string, unknown>
): void {
  if (!isRumEnabled()) return;

  try {
    datadogRum.addAction(metricName, {
      metric_value: value,
      timestamp: Date.now(),
      ...context,
    });

    // If a numeric value is provided, also log it as a timing
    if (typeof value === "number") {
      datadogRum.addTiming(metricName, value);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to add custom metric ${metricName}:`, error);
  }
}

/**
 * Track a user interaction
 * Use this to track custom user interactions that aren't automatically captured
 *
 * @param interactionName - Name of the interaction (e.g., 'filter_applied', 'search_performed')
 * @param context - Additional context data
 */
export function trackUserInteraction(
  interactionName: string,
  context?: Record<string, unknown>
): void {
  if (!isRumEnabled()) return;

  try {
    datadogRum.addAction(`user_interaction_${interactionName}`, {
      interaction: interactionName,
      timestamp: Date.now(),
      page_path: window.location.pathname,
      ...context,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to track user interaction ${interactionName}:`,
      error
    );
  }
}

/**
 * Track an error
 * Use this to manually report errors to Datadog
 *
 * @param error - Error object or message
 * @param context - Additional context data
 */
export function trackError(
  error: Error | string,
  context?: Record<string, unknown>
): void {
  if (!isRumEnabled()) return;

  try {
    const errorObj = typeof error === "string" ? new Error(error) : error;

    datadogRum.addError(errorObj, {
      timestamp: Date.now(),
      page_path: window.location.pathname,
      ...context,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Failed to track error:", err);
  }
}

/**
 * Add global context that will be attached to all RUM events
 * Use this for context that doesn't change frequently (e.g., feature flags, A/B test variants)
 *
 * @param key - Context key
 * @param value - Context value
 */
export function addGlobalContext(key: string, value: unknown): void {
  if (!isRumEnabled()) return;

  try {
    datadogRum.setGlobalContextProperty(key, value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to add global context ${key}:`, error);
  }
}

/**
 * Remove global context
 *
 * @param key - Context key to remove
 */
export function removeGlobalContext(key: string): void {
  if (!isRumEnabled()) return;

  try {
    datadogRum.removeGlobalContextProperty(key);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to remove global context ${key}:`, error);
  }
}

/**
 * Start a performance measurement
 * Returns a function to stop the measurement and report it
 *
 * @param measurementName - Name of the measurement
 * @returns Function to stop the measurement
 */
export function startMeasurement(measurementName: string): () => void {
  if (!isRumEnabled()) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {}; // No-op function
  }

  const startTime = performance.now();

  return (context?: Record<string, unknown>) => {
    try {
      const duration = performance.now() - startTime;

      datadogRum.addAction(measurementName, {
        duration_ms: duration,
        timestamp: Date.now(),
        ...context,
      });

      datadogRum.addTiming(measurementName, duration);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `Failed to complete measurement ${measurementName}:`,
        error
      );
    }
  };
}
